import { extractValue } from 'utils/sqlHelpers';

/**
 * Verification Error Handler Helper
 * Provides reusable error handling functions for verification flows
 */

/**
 * Handles and formats verification errors into user-friendly messages
 * @param {Error} error - The error object to handle
 * @returns {string} User-friendly error message
 */

export const VERIFICATION_STATUS = {
  LOADING: 'loading',
  SUCCESS: 'success',
  EXPIRED: 'expired',
  ERROR: 'error',
  SERVER_ERROR: 'server_error',
  NETWORK_ERROR: 'network_error',
  NOT_FOUND: 'not_found',
  UNAUTHORIZED: 'unauthorized',
};

const {
  ERROR,
  SUCCESS,
  EXPIRED,
  SERVER_ERROR,
  NETWORK_ERROR,
  NOT_FOUND,
  UNAUTHORIZED,
} = VERIFICATION_STATUS;

export const handleVerificationError = (data) => {
  if (process.env.NODE_ENV === 'development') {
    console.error('Verification error:', data);
  }

  // Early return for invalid input
  if (!data) {
    return {
      errorMessage: 'An error occurred during verification. Please try again.',
      status: ERROR,
    };
  }

  // Extract status and error message efficiently
  const status = data?.status ?? data?.response?.status;
  const error = extractErrorMessage(data);

  if (process.env.NODE_ENV === 'development') {
    console.error('Processed error:', { error, status, originalData: data });
  }

  // Handle by status code first (most reliable)
  if (status) {
    const statusResult = handleStatusError(status, error);
    if (statusResult) return statusResult;
  }

  // Fallback to message parsing
  return handleMessageError(error);
};

// Extract error message from different error structures
const extractErrorMessage = (data) => {
  return (
    data?.response?.data?.message ??
    data?.response?.data?.error ??
    data?.message ??
    (typeof data === 'string' ? data : null) ??
    data?.toString() ??
    'Unknown error occurred'
  );
};

// Handle errors by HTTP status code
const handleStatusError = (status, error) => {
  const statusHandlers = {
    429: () => ({
      errorMessage:
        error || 'Too many requests. Please wait a moment and try again.',
      status: 'rate_limited',
    }),
    404: () => ({
      errorMessage: 'Verification endpoint not found. Please contact support.',
      status: NOT_FOUND,
    }),
    401: () => ({
      errorMessage: 'Authentication failed. Please try again.',
      status: UNAUTHORIZED,
    }),
    403: () => ({
      errorMessage: 'Access denied. Please contact support.',
      status: 'forbidden',
    }),
    500: () => ({
      errorMessage: 'Server error. Please try again later.',
      status: 'server_error',
    }),
  };

  const handler = statusHandlers[status];
  return handler ? handler() : null;
};

// Handle errors by message content
const handleMessageError = (error) => {
  const errorMessageStr =
    typeof error === 'object'
      ? error.message || error.toString()
      : String(error);

  // Define error patterns with their handlers
  const errorPatterns = [
    {
      patterns: ['Unexpected token', '<!DOCTYPE', 'JSON'],
      handler: () => ({
        errorMessage:
          'Server returned invalid response. Please contact support.',
        status: SERVER_ERROR,
      }),
    },
    {
      patterns: [
        'Failed to fetch',
        'NetworkError',
        'ECONNREFUSED',
        'timeout',
        'ERR_NETWORK',
      ],
      handler: () => ({
        errorMessage:
          'Network error. Please check your connection and try again.',
        status: NETWORK_ERROR,
      }),
    },
    {
      patterns: ['404', 'Not Found'],
      handler: () => ({
        errorMessage:
          'Verification endpoint not found. Please contact support.',
        status: NOT_FOUND,
      }),
    },
    {
      patterns: ['401', 'Unauthorized'],
      handler: () => ({
        errorMessage: 'Authentication failed. Please try again.',
        status: UNAUTHORIZED,
      }),
    },
    {
      patterns: ['403', 'Forbidden'],
      handler: () => ({
        errorMessage: 'Access denied. Please contact support.',
        status: 'forbidden',
      }),
    },
    {
      patterns: ['500', 'Internal Server Error'],
      handler: () => ({
        errorMessage: 'Server error. Please try again later.',
        status: 'server_error',
      }),
    },
    {
      patterns: ['429', 'Too Many Requests'],
      handler: () => ({
        errorMessage:
          error || 'Too many requests. Please wait a moment and try again.',
        status: 'rate_limited',
      }),
    },
    {
      patterns: ['token', 'Token', 'expired', 'invalid'],
      handler: () => ({
        errorMessage:
          'Verification link is invalid or has expired. Please request a new one.',
        status: EXPIRED,
      }),
    },
    {
      patterns: ['CORS', 'Cross-Origin'],
      handler: () => ({
        errorMessage: 'Configuration error. Please contact support.',
        status: 'cors_error',
      }),
    },
  ];

  // Find matching pattern and return its handler result
  for (const { patterns, handler } of errorPatterns) {
    if (patterns.some((pattern) => errorMessageStr.includes(pattern))) {
      return handler();
    }
  }

  // Default fallback
  return {
    errorMessage: 'An error occurred during verification. Please try again.',
    status: ERROR,
  };
};

/**
 * Validates verification response structure
 * @param {any} result - The response to validate
 * @returns {boolean} True if valid, throws error if invalid
 * @throws {Error} If response is invalid
 */
export const validateVerificationResponse = (result) => {
  if (!result || typeof result !== 'object') {
    throw new Error('Invalid response format from verification service');
  }

  return true;
};

/**
 * Validates verification result structure
 * @param {any} result - The result to validate
 * @returns {boolean} True if valid, throws error if invalid
 * @throws {Error} If result is invalid
 */
export const validateVerificationResult = (result) => {
  if (!result || typeof result !== 'object') {
    throw new Error('Invalid verification result format');
  }

  return true;
};

/**
 * Determines verification status based on result
 * @param {Object} result - Axios response object or verification result
 * @returns {string} Verification status ('success', 'error', 'expired')
 */
export const getVerificationStatus = (result) => {
  if (!result || typeof result !== 'object') {
    return 'error';
  }

  // Handle axios response (status at top level) or success data
  if (result.status === 200 || result.data?.success === true) {
    return 'success';
  }

  if (result.error?.includes('expired') || result.error?.includes('token')) {
    return 'expired';
  }

  return 'error';
};

/**
 * Extracts user information from verification result
 * @param {Object} result - Verification result
 * @returns {Object|null} User information or null
 */
export const extractUserInfo = (result) => {
  try {
    const user = result?.data?.user;
    if (!user) return null;

    return {
      id: user.id,
      uuid: user.uuid,
      username: user.username,
      email: user.email,
      first_name: extractValue(user.first_name, ''),
      last_name: extractValue(user.last_name, ''),
      role: user.role,
      phone_number: extractValue(user.phone_number, ''),
      invited_by: extractValue(user.invited_by, ''),
      created_at: user.created_at,
      updated_at: user.updated_at,
      deleted_at: extractValue(user.deleted_at, ''),
      google_id: extractValue(user.google_id, ''),
      oauth_provider: extractValue(user.oauth_provider, ''),
      avatar_url: extractValue(user.avatar_url, ''),
      email_verified: user.email_verified,
    };
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to extract user info:', error);
    }
    return null;
  }
};

/**
 * Creates a standardized verification result object
 * @param {boolean} success - Whether verification was successful
 * @param {string} message - Result message
 * @param {Object} data - Additional data (optional)
 * @param {string} error - Error message (optional)
 * @returns {Object} Standardized verification result
 */
export const createVerificationResult = (
  success,
  message,
  data = null,
  error = null
) => {
  const result = {
    success,
    message,
  };

  if (data) {
    result.data = data;
  }

  if (error) {
    result.error = error;
  }

  return result;
};

/**
 * Checks if a token is valid format
 * @param {string} token - Token to validate
 * @returns {boolean} True if token appears valid
 */
export const isValidTokenFormat = (token) => {
  if (!token || typeof token !== 'string') {
    return false;
  }

  // Basic validation - should be alphanumeric string of reasonable length
  return /^[a-zA-Z0-9+/=_-]{20,}$/.test(token.trim());
};

/**
 * Masks token for display purposes
 * @param {string} token - Token to mask
 * @param {number} visibleChars - Number of characters to show at start and end
 * @returns {string} Masked token
 */
export const maskToken = (token, visibleChars = 8) => {
  if (!token || typeof token !== 'string') {
    return '';
  }

  const trimmed = token.trim();
  if (trimmed.length <= visibleChars * 2) {
    return trimmed;
  }

  const start = trimmed.substring(0, visibleChars);
  const end = trimmed.substring(trimmed.length - visibleChars);
  const middle = '*'.repeat(Math.min(trimmed.length - visibleChars * 2, 20));

  return `${start}${middle}${end}`;
};
